import React, { useCallback, useEffect, useRef } from "react";
import { useHorizontalScrollOverflow } from "../shared/hooks/useHorizontalScrollOverflow";

/**
 * Wraps a horizontal scroller and shows left/right edge fades + chevrons when
 * more content is available off-screen (mobile tab bars, top nav, etc.).
 * Chevrons are clickable; vertical wheel over the scroller scrolls horizontally
 * when there is overflow (desktop mice have no easy horizontal scroll).
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {string} [props.className] Extra classes on the outer hint shell (e.g. `h-scroll-hint--nav`)
 * @param {string} [props.scrollerClassName] Classes on the scrolling element (e.g. `tabs-list`)
 * @param {keyof JSX.IntrinsicElements} [props.as] Scroller element type (default `div`; use `ul` for nav)
 */
export default function HorizontalScrollHint({
  children,
  className = "",
  scrollerClassName = "",
  as: ScrollerTag = "div",
  ...scrollerProps
}) {
  const ref = useRef(null);
  const { canScrollLeft, canScrollRight } = useHorizontalScrollOverflow(ref);
  const fits = !canScrollLeft && !canScrollRight;

  const scrollByPage = useCallback((direction) => {
    const el = ref.current;
    if (!el) return;
    const delta = Math.max(120, Math.floor(el.clientWidth * 0.75)) * direction;
    el.scrollBy({ left: delta, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth + 2) return;
      // Prefer explicit horizontal deltas; otherwise map vertical wheel to horizontal.
      const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!dx) return;
      const atStart = el.scrollLeft <= 2;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
      if ((dx < 0 && atStart) || (dx > 0 && atEnd)) return;
      e.preventDefault();
      el.scrollLeft += dx;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [canScrollLeft, canScrollRight]);

  const shellClass = [
    "h-scroll-hint",
    canScrollLeft ? "h-scroll-hint--left" : "",
    canScrollRight ? "h-scroll-hint--right" : "",
    fits ? "h-scroll-hint--fits" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const scrollerClass = ["h-scroll-hint__scroller", scrollerClassName].filter(Boolean).join(" ");

  return (
    <div className={shellClass}>
      <ScrollerTag ref={ref} className={scrollerClass} {...scrollerProps}>
        {children}
      </ScrollerTag>
      {canScrollLeft ? (
        <button
          type="button"
          className="h-scroll-hint__edge h-scroll-hint__edge--left"
          aria-label="Scroll menu left"
          onClick={() => scrollByPage(-1)}
        />
      ) : null}
      {canScrollRight ? (
        <button
          type="button"
          className="h-scroll-hint__edge h-scroll-hint__edge--right"
          aria-label="Scroll menu right"
          onClick={() => scrollByPage(1)}
        />
      ) : null}
    </div>
  );
}
